import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { theme } from '../muiTheme';
import { Popper, Paper, Typography, ClickAwayListener, LinearProgress, Box } from '@mui/material';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import { useAuthStore } from '../../contexts/AuthContext';
import { memo } from 'react';
import useGetAccountLimits from '../../helpers/useAccountLimits';
const ACCOUNT_LIMITS = ["integrations", "nativeDashboards", "adminSeats", "nonAdminSeats"];
/**
 * A reusable icon button component that displays account usage limits.
 * 
 *
 * @param {object} props - The component properties.
 * @param {boolean} props.open - Whether the popper is open.
 * @param {HTMLElement} props.anchorEl - The element to anchor the popper to.
 * @param {string} [props.placement] - The placement of the popper.
 * @param {object} [props.style] - Custom styles for the popper.
 * @param {Array} [props.modifiers] - Popper.js modifiers.
 * @param {function} props.onClickAway - Callback function to run when clicking away from the popper.
 * @param {React.Ref} props.buttonRef - Ref for the button element.
 * @param {string} [props.iconColor] - Color of the icon.
 * @param {function} props.handleClick - Callback function to run when the button is clicked.
 * @param {object} [props.linearProgressStyle] - Custom styles for the linear progress bar.
 * @returns {JSX.Element} A reusable icon button component that displays account usage limits.
*/
function AccountUsageIcon({
    open,
    anchorEl,
    placement,
    style,
    modifiers,
    onClickAway,
    buttonRef,
    iconColor,
    handleClick,
    linearProgressStyle = {},
    iconSize,
    generalStyle = {},
}) {
    const { selectedAccount } = useAuthStore();
    const accountId = selectedAccount?.id;
    const accountLimits = useGetAccountLimits(accountId);

    if (accountLimits.error || accountLimits.loading) {
        return null; 
    }
    return (
        <>
            <Grid item sx={{
                ...generalStyle
            }}>
                <IconButton
                    ref={buttonRef}
                    sx={{
                        color: iconColor ? iconColor : theme.palette.white.main,
                        height: iconSize ? iconSize : '24px',
                        padding: '0px',
                    }}
                    onClick={handleClick}
                >
                    <ArrowCircleUpIcon fontSize='inherit' />
                </IconButton>
            </Grid>
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement={placement ? placement : "bottom-start"}
                style={style ? style : { zIndex: 1300 }}
                modifiers={modifiers ? modifiers : [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                ]}
            >
                <ClickAwayListener onClickAway={onClickAway}>
                    <Paper elevation={3} sx={{ width: 300, p: 1 }}>
                        {ACCOUNT_LIMITS.map((key) => {
                            const { percentage, isUnlimited, limit, available, name } = accountLimits[key];
                            return (
                                <Box key={key} display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                                    <Typography sx={{
                                        flex: 1,
                                        color: "var(--Blacks-500, #100C08)",
                                        fontFamily: "Lato",
                                        fontWeight: 400,
                                        fontSize: "10px"
                                    }}>{name}</Typography>
                                    <Box sx={{ flex: 2, mx: 1 }}>
                                        {limit != null ? (
                                            <LinearProgress
                                                variant="determinate"
                                                value={percentage}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: "#E2E6E9",
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: "#00A6FF",
                                                    },
                                                    ...linearProgressStyle,
                                                }}
                                            />
                                        ) : (
                                            <LinearProgress
                                                variant="determinate"
                                                value={0}
                                                sx={{
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: "#E2E6E9",
                                                    ...linearProgressStyle
                                                }}
                                            />
                                        )}

                                    </Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: isUnlimited || available > 0 ? 'green' : 'red',
                                            fontWeight: 400,
                                            minWidth: '80px',
                                            textAlign: 'right',
                                            fontFamily: 'Lato',
                                            fontSize: '10px',
                                        }}
                                    >
                                        {isUnlimited ? 'Unlimited' : `${available} available`}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>

    )
}
export default memo(AccountUsageIcon);